import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  CircleHelp,
  ClipboardList,
  Clock3,
  ExternalLink,
  FileImage,
  ImagePlus,
  Mail,
  Menu,
  MessageSquareText,
  Pencil,
  Save,
  School,
  Sparkles,
  Trash2,
} from "lucide-react";
import Button from "../components/Button";
import DashboardEmptyState from "../components/dashboard/DashboardEmptyState";
import SchoolDashboardSidebar from "../components/dashboard/SchoolDashboardSidebar";
import { CITIES, CURRICULA, LEVELS } from "../data/schools";
import { formatRupiah } from "../lib/format";
import brandLogo from "../assets/images/MySchoolMate.png";
import SEO from "../components/SEO";

const STORAGE_KEY = "msm_school_profile";
const REPRESENTATIVE_KEY = "msm_school_representative";

const stepLabels = [
  "Informasi Dasar",
  "Akademik & Program",
  "Biaya",
  "Fasilitas & Ekstrakurikuler",
  "Galeri & Kontak",
  "Review & Simpan",
];

const interestOptions = ["Coding", "Tahfidz", "STEM", "Montessori", "Bilingual", "Sains", "Olahraga", "Seni", "Bahasa"];
const facilityOptions = ["Perpustakaan", "Lab Komputer", "Lab Sains", "Masjid", "Lapangan Olahraga", "Kantin Sehat", "Ruang Seni", "Kolam Renang", "Auditorium"];
const extracurricularOptions = ["Coding Club", "Futsal", "Robotik", "Pramuka", "Tahfidz", "Basket", "Debate Club", "Paduan Suara"];

const emptyProfile = {
  id: "school-profile",
  slug: "",
  name: "",
  educationLevels: [],
  city: "",
  district: "",
  address: "",
  description: "",
  accreditation: "",
  specialNeedsSupport: false,
  curriculum: "",
  studentTeacherRatio: "",
  religiousPrograms: "",
  specialPrograms: "",
  interests: [],
  monthlyTuition: "",
  entranceFee: "",
  feeNotes: "",
  facilities: [],
  extracurriculars: [],
  images: [],
  phone: "",
  email: "",
  websiteUrl: "",
  completed: false,
  updatedAt: null,
  activities: [],
};

function normalizeProfile(value) {
  if (!value || typeof value !== "object") return { ...emptyProfile };
  return {
    ...emptyProfile,
    ...value,
    educationLevels: Array.isArray(value.educationLevels) ? value.educationLevels : [],
    interests: Array.isArray(value.interests) ? value.interests : [],
    facilities: Array.isArray(value.facilities) ? value.facilities : [],
    extracurriculars: Array.isArray(value.extracurriculars) ? value.extracurriculars : [],
    images: Array.isArray(value.images) ? value.images : [],
    activities: Array.isArray(value.activities) ? value.activities : [],
  };
}

function readStoredProfile() {
  try {
    return normalizeProfile(JSON.parse(localStorage.getItem(STORAGE_KEY)));
  } catch {
    return { ...emptyProfile };
  }
}

function readRepresentativeName() {
  try {
    const parsed = JSON.parse(localStorage.getItem(REPRESENTATIVE_KEY));
    return parsed?.name?.trim() || parsed?.fullName?.trim() || "Pengguna";
  } catch {
    return "Pengguna";
  }
}

const completionChecks = [
  { label: "Nama dan jenjang sekolah", step: 1, test: (p) => p.name.trim() && p.educationLevels.length },
  { label: "Lokasi dan alamat sekolah", step: 1, test: (p) => p.city.trim() && p.address.trim() },
  { label: "Deskripsi sekolah", step: 1, test: (p) => p.description.trim() },
  { label: "Kurikulum", step: 2, test: (p) => p.curriculum.trim() },
  { label: "Informasi biaya", step: 3, test: (p) => Number(p.monthlyTuition) > 0 },
  { label: "Fasilitas sekolah", step: 4, test: (p) => p.facilities.length > 0 },
  { label: "Minimal 3 foto sekolah", step: 5, test: (p) => p.images.length >= 3 },
  { label: "Kontak sekolah", step: 5, test: (p) => p.phone.trim() && /^\S+@\S+\.\S+$/.test(p.email) },
];

function getCompletion(profile) {
  const complete = completionChecks.filter((item) => item.test(profile)).length;
  return Math.round((complete / completionChecks.length) * 100);
}

function getTasks(profile) {
  return completionChecks.filter((item) => !item.test(profile));
}

function formatDate(value) {
  if (!value) return null;
  return new Intl.DateTimeFormat("id-ID", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatCurrencyInput(value) {
  if (!value) return "";
  return new Intl.NumberFormat("id-ID").format(Number(value));
}

function Field({ label, required, error, children, hint }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-ink">
        {label}{required ? <span className="ml-1 text-coral">*</span> : null}
      </span>
      {children}
      {hint && !error ? <span className="mt-1.5 block text-xs font-medium text-ink/45">{hint}</span> : null}
      {error ? <span className="mt-1.5 block text-xs font-bold text-coral">{error}</span> : null}
    </label>
  );
}

const inputClass = "min-h-12 w-full rounded-xl border border-ink/15 bg-white px-4 text-sm font-medium text-ink outline-none transition focus:border-[#F7AB2D] focus:ring-4 focus:ring-[#F7AB2D]/15";

function ToggleChips({ label, options, values, onChange, required, error }) {
  const toggle = (option) => onChange(values.includes(option) ? values.filter((item) => item !== option) : [...values, option]);
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-bold text-ink">{label}{required ? <span className="ml-1 text-coral">*</span> : null}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = values.includes(option);
          return (
            <label key={option} className={`cursor-pointer rounded-xl border px-3.5 py-2 text-sm font-bold transition focus-within:ring-4 focus-within:ring-[#F7AB2D]/15 ${selected ? "border-[#F7AB2D] bg-[#FFF3D7] text-ink" : "border-ink/12 bg-white text-ink/60 hover:border-ink/25"}`}>
              <input type="checkbox" className="sr-only" checked={selected} onChange={() => toggle(option)} />
              <span className="inline-flex items-center gap-1.5">{selected ? <Check className="h-3.5 w-3.5 text-[#B46F00]" aria-hidden="true" /> : null}{option}</span>
            </label>
          );
        })}
      </div>
      {error ? <p className="mt-2 text-xs font-bold text-coral">{error}</p> : null}
    </fieldset>
  );
}

function Stepper({ step, canJump, onStep }) {
  return (
    <div className="border-b border-ink/10 px-5 py-5 sm:px-7">
      <div className="md:hidden">
        <div className="flex items-center justify-between text-xs font-bold text-ink/50"><span>Langkah {step} dari 6</span><span>{Math.round((step / 6) * 100)}%</span></div>
        <h2 className="mt-1 text-lg font-extrabold text-ink">{stepLabels[step - 1]}</h2>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-ink/8"><div className="h-full rounded-full bg-[#F7AB2D]" style={{ width: `${(step / 6) * 100}%` }} /></div>
      </div>
      <ol className="hidden grid-cols-6 gap-2 md:grid" aria-label="Tahapan data sekolah">
        {stepLabels.map((label, index) => {
          const number = index + 1;
          const completed = number < step;
          const active = number === step;
          return (
            <li key={label} className="relative min-w-0">
              {index < 5 ? <span className={`absolute left-[55%] top-4 h-px w-[90%] ${completed ? "bg-mint" : "bg-ink/12"}`} aria-hidden="true" /> : null}
              <button type="button" disabled={!canJump && number > step} onClick={() => onStep(number)} className="relative z-10 flex w-full flex-col items-center rounded-lg text-center focus-ring disabled:cursor-default">
                <span className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-extrabold ${completed ? "border-mint bg-mint text-ink" : active ? "border-[#F7AB2D] bg-[#F7AB2D] text-ink" : "border-ink/12 bg-white text-ink/40"}`}>{completed ? <Check className="h-4 w-4" /> : number}</span>
                <span className={`mt-2 max-w-[120px] text-[10px] font-bold leading-tight ${active ? "text-ink" : "text-ink/45"}`}>{label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function validateStep(step, draft) {
  const errors = {};
  if (step === 1) {
    if (!draft.name.trim()) errors.name = "Nama sekolah wajib diisi.";
    if (!draft.educationLevels.length) errors.educationLevels = "Pilih minimal satu jenjang.";
    if (!draft.city.trim()) errors.city = "Kota wajib dipilih.";
    if (!draft.address.trim()) errors.address = "Alamat lengkap wajib diisi.";
    if (!draft.description.trim()) errors.description = "Deskripsi sekolah wajib diisi.";
  }
  if (step === 2 && !draft.curriculum.trim()) errors.curriculum = "Kurikulum wajib dipilih.";
  if (step === 3 && !(Number(draft.monthlyTuition) > 0)) errors.monthlyTuition = "SPP bulanan wajib diisi.";
  if (step === 4 && !draft.facilities.length) errors.facilities = "Pilih minimal satu fasilitas.";
  if (step === 5) {
    if (draft.images.length < 3) errors.images = "Tambahkan minimal 3 foto sekolah.";
    if (!draft.phone.trim()) errors.phone = "Nomor telepon sekolah wajib diisi.";
    if (!/^\S+@\S+\.\S+$/.test(draft.email)) errors.email = "Masukkan email sekolah yang valid.";
    if (draft.websiteUrl && !/^https?:\/\//i.test(draft.websiteUrl)) errors.websiteUrl = "Gunakan URL lengkap yang diawali http:// atau https://.";
  }
  return errors;
}

function SchoolDataForm({ profile, onSave, initialStep = 1, onCancel }) {
  const [draft, setDraft] = useState(() => normalizeProfile(profile));
  const [step, setStep] = useState(Math.min(6, Math.max(1, initialStep)));
  const [errors, setErrors] = useState({});
  const [imageUrl, setImageUrl] = useState("");
  const isExisting = profile.completed;
  const dirty = JSON.stringify(draft) !== JSON.stringify(profile);

  useEffect(() => {
    const warn = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const update = (name, value) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const next = () => {
    const nextErrors = validateStep(step, draft);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setStep((current) => Math.min(6, current + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addImageUrl = () => {
    const value = imageUrl.trim();
    if (!/^https?:\/\//i.test(value)) {
      setErrors((current) => ({ ...current, images: "Masukkan URL gambar yang valid." }));
      return;
    }
    update("images", [...draft.images, value].slice(0, 8));
    setImageUrl("");
  };

  const addFiles = (event) => {
    const files = Array.from(event.target.files || []).slice(0, 8 - draft.images.length);
    const invalid = files.find((file) => file.size > 1024 * 1024);
    if (invalid) {
      setErrors((current) => ({ ...current, images: "Setiap gambar maksimal 1 MB untuk penyimpanan lokal." }));
      event.target.value = "";
      return;
    }
    Promise.all(files.map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    }))).then((images) => update("images", [...draft.images, ...images].slice(0, 8)));
    event.target.value = "";
  };

  const submit = () => {
    const allErrors = [1, 2, 3, 4, 5].map((number) => [number, validateStep(number, draft)]).find(([, value]) => Object.keys(value).length);
    if (allErrors) {
      setStep(allErrors[0]);
      setErrors(allErrors[1]);
      return;
    }
    onSave({ ...draft, completed: true });
  };

  return (
    <section className="overflow-hidden rounded-[22px] border border-ink/10 bg-white shadow-sm">
      <Stepper step={step} canJump={isExisting} onStep={setStep} />
      <div className="p-5 sm:p-7 lg:p-8">
        <div className="mb-7">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#B46F00]">Langkah {step}</p>
          <h2 className="mt-1 text-2xl font-extrabold text-ink">{stepLabels[step - 1]}</h2>
        </div>

        {step === 1 ? (
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Nama Sekolah" required error={errors.name}><input value={draft.name} onChange={(e) => update("name", e.target.value)} className={inputClass} placeholder="Masukkan nama sekolah" /></Field>
            <ToggleChips label="Jenjang" required options={LEVELS} values={draft.educationLevels} onChange={(value) => update("educationLevels", value)} error={errors.educationLevels} />
            <Field label="Kota" required error={errors.city}>
              <select value={draft.city} onChange={(e) => update("city", e.target.value)} className={inputClass}><option value="">Pilih kota</option>{CITIES.map((city) => <option key={city}>{city}</option>)}</select>
            </Field>
            <Field label="Kecamatan / Area"><input value={draft.district} onChange={(e) => update("district", e.target.value)} className={inputClass} placeholder="Contoh: Antapani" /></Field>
            <Field label="Alamat Lengkap" required error={errors.address}><textarea value={draft.address} onChange={(e) => update("address", e.target.value)} className={`${inputClass} min-h-[110px] py-3`} placeholder="Masukkan alamat lengkap" /></Field>
            <Field label="Deskripsi Sekolah" required error={errors.description}><textarea value={draft.description} onChange={(e) => update("description", e.target.value)} className={`${inputClass} min-h-[110px] py-3`} placeholder="Ceritakan pendekatan dan lingkungan sekolah" /></Field>
            <Field label="Akreditasi"><select value={draft.accreditation} onChange={(e) => update("accreditation", e.target.value)} className={inputClass}><option value="">Pilih akreditasi</option>{["A", "B", "C", "Belum tersedia"].map((item) => <option key={item}>{item}</option>)}</select></Field>
            <label className="flex min-h-12 items-center gap-3 self-end rounded-xl border border-ink/12 bg-warm px-4 text-sm font-bold text-ink"><input type="checkbox" checked={draft.specialNeedsSupport} onChange={(e) => update("specialNeedsSupport", e.target.checked)} className="h-4 w-4 accent-[#F7AB2D]" />Mendukung kebutuhan khusus</label>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Kurikulum" required error={errors.curriculum}><select value={draft.curriculum} onChange={(e) => update("curriculum", e.target.value)} className={inputClass}><option value="">Pilih kurikulum</option>{CURRICULA.map((item) => <option key={item}>{item}</option>)}</select></Field>
            <Field label="Rasio Guru : Siswa" hint="Masukkan jumlah siswa untuk setiap guru."><input type="number" min="1" value={draft.studentTeacherRatio} onChange={(e) => update("studentTeacherRatio", e.target.value)} className={inputClass} placeholder="Contoh: 12" /></Field>
            <Field label="Program Keagamaan"><input value={draft.religiousPrograms} onChange={(e) => update("religiousPrograms", e.target.value)} className={inputClass} placeholder="Contoh: Tahfidz" /></Field>
            <Field label="Program Khusus"><input value={draft.specialPrograms} onChange={(e) => update("specialPrograms", e.target.value)} className={inputClass} placeholder="Contoh: Bilingual" /></Field>
            <div className="sm:col-span-2"><ToggleChips label="Minat / Fokus Pendidikan" options={interestOptions} values={draft.interests} onChange={(value) => update("interests", value)} /></div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="SPP Bulanan" required error={errors.monthlyTuition}><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-ink/45">Rp</span><input inputMode="numeric" value={formatCurrencyInput(draft.monthlyTuition)} onChange={(e) => update("monthlyTuition", e.target.value.replace(/\D/g, ""))} className={`${inputClass} pl-11`} placeholder="1.800.000" /></div></Field>
            <Field label="Uang Pangkal"><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-ink/45">Rp</span><input inputMode="numeric" value={formatCurrencyInput(draft.entranceFee)} onChange={(e) => update("entranceFee", e.target.value.replace(/\D/g, ""))} className={`${inputClass} pl-11`} placeholder="12.000.000" /></div></Field>
            <div className="sm:col-span-2"><Field label="Catatan Biaya"><textarea value={draft.feeNotes} onChange={(e) => update("feeNotes", e.target.value)} className={`${inputClass} min-h-[110px] py-3`} placeholder="Informasi tambahan mengenai komponen biaya" /></Field></div>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="space-y-7">
            <ToggleChips label="Fasilitas" required options={facilityOptions} values={draft.facilities} onChange={(value) => update("facilities", value)} error={errors.facilities} />
            <ToggleChips label="Ekstrakurikuler" options={extracurricularOptions} values={draft.extracurriculars} onChange={(value) => update("extracurriculars", value)} />
          </div>
        ) : null}

        {step === 5 ? (
          <div className="space-y-7">
            <div>
              <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end"><div><h3 className="text-sm font-bold text-ink">Galeri Sekolah <span className="text-coral">*</span></h3><p className="mt-1 text-xs font-medium text-ink/45">Tambahkan 3–8 foto. Unggahan lokal maksimal 1 MB per gambar.</p></div><span className="text-xs font-bold text-ink/50">{draft.images.length}/8 foto</span></div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {draft.images.map((image, index) => (
                  <div key={`${image.slice(0, 30)}-${index}`} className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-ink/10 bg-warm"><img src={image} alt={`Pratinjau foto sekolah ${index + 1}`} className="h-full w-full object-cover" /><button type="button" onClick={() => update("images", draft.images.filter((_, itemIndex) => itemIndex !== index))} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-coral shadow-sm focus-ring" aria-label={`Hapus foto ${index + 1}`}><Trash2 className="h-4 w-4" /></button></div>
                ))}
                {draft.images.length < 8 ? <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-ink/20 bg-warm text-center text-ink/50 hover:border-[#F7AB2D] focus-within:ring-4 focus-within:ring-[#F7AB2D]/15"><ImagePlus className="h-6 w-6" /><span className="mt-2 text-xs font-bold">Unggah foto</span><input type="file" accept="image/*" multiple className="sr-only" onChange={addFiles} /></label> : null}
              </div>
              <div className="mt-3 flex gap-2"><input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={inputClass} placeholder="Atau masukkan URL gambar" /><button type="button" onClick={addImageUrl} className="shrink-0 rounded-xl border border-ink bg-white px-4 text-sm font-bold text-ink hover:bg-warm focus-ring">Tambah</button></div>
              {errors.images ? <p className="mt-2 text-xs font-bold text-coral">{errors.images}</p> : null}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Nomor Telepon Sekolah" required error={errors.phone}><input value={draft.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} placeholder="Contoh: 022 1234567" /></Field>
              <Field label="Email Sekolah" required error={errors.email}><input type="email" value={draft.email} onChange={(e) => update("email", e.target.value)} className={inputClass} placeholder="info@namasekolah.sch.id" /></Field>
              <div className="sm:col-span-2"><Field label="Situs Sekolah" error={errors.websiteUrl} hint="Opsional. URL ini dapat digunakan sebagai tautan situs sekolah di masa mendatang."><input type="url" value={draft.websiteUrl} onChange={(e) => update("websiteUrl", e.target.value)} className={inputClass} placeholder="https://www.namasekolah.sch.id" /></Field></div>
            </div>
          </div>
        ) : null}

        {step === 6 ? <ReviewSummary profile={draft} onEdit={setStep} /> : null}

        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-ink/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            {step > 1 ? <button type="button" onClick={() => setStep((current) => current - 1)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-ink/15 bg-white px-5 text-sm font-bold text-ink hover:bg-warm focus-ring"><ArrowLeft className="h-4 w-4" />Kembali</button> : null}
            {onCancel ? <button type="button" onClick={onCancel} className="min-h-11 rounded-xl px-4 text-sm font-bold text-ink/55 hover:text-ink focus-ring">Batal</button> : null}
          </div>
          {step < 6 ? <button type="button" onClick={next} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#F7AB2D] px-6 text-sm font-extrabold text-ink hover:brightness-95 focus-ring">Lanjutkan<ArrowRight className="h-4 w-4" /></button> : <button type="button" onClick={submit} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-ink px-6 text-sm font-extrabold text-white hover:bg-[#2c2363] focus-ring"><Save className="h-4 w-4" />Simpan Data Sekolah</button>}
        </div>
      </div>
    </section>
  );
}

function ReviewBlock({ title, step, onEdit, children }) {
  return <section className="rounded-[18px] border border-ink/10 bg-warm p-5"><div className="flex items-center justify-between gap-4"><h3 className="font-extrabold text-ink">{title}</h3><button type="button" onClick={() => onEdit(step)} className="inline-flex items-center gap-1 text-xs font-extrabold text-purple hover:underline focus-ring"><Pencil className="h-3.5 w-3.5" />Edit</button></div><div className="mt-3 text-sm font-medium leading-relaxed text-ink/60">{children}</div></section>;
}

function ReviewSummary({ profile, onEdit }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ReviewBlock title="Informasi Dasar" step={1} onEdit={onEdit}><p className="font-bold text-ink">{profile.name || "Belum diisi"}</p><p>{[profile.city, profile.district].filter(Boolean).join(", ") || "Lokasi belum diisi"}</p><p>{profile.educationLevels.join(", ") || "Jenjang belum dipilih"}</p></ReviewBlock>
      <ReviewBlock title="Akademik" step={2} onEdit={onEdit}><p>{profile.curriculum || "Kurikulum belum diisi"}</p><p>{profile.studentTeacherRatio ? `Rasio guru:siswa 1:${profile.studentTeacherRatio}` : "Rasio belum diisi"}</p><p>{[profile.religiousPrograms, profile.specialPrograms].filter(Boolean).join(" · ") || "Program tambahan belum diisi"}</p></ReviewBlock>
      <ReviewBlock title="Biaya" step={3} onEdit={onEdit}><p>SPP: {profile.monthlyTuition ? formatRupiah(Number(profile.monthlyTuition)) : "Belum diisi"}</p><p>Uang pangkal: {profile.entranceFee ? formatRupiah(Number(profile.entranceFee)) : "Belum diisi"}</p></ReviewBlock>
      <ReviewBlock title="Fasilitas & Ekstrakurikuler" step={4} onEdit={onEdit}><p>{profile.facilities.join(", ") || "Fasilitas belum dipilih"}</p><p className="mt-1">{profile.extracurriculars.join(", ") || "Ekstrakurikuler belum dipilih"}</p></ReviewBlock>
      <ReviewBlock title="Galeri" step={5} onEdit={onEdit}><p>{profile.images.length} foto ditambahkan</p></ReviewBlock>
      <ReviewBlock title="Kontak" step={5} onEdit={onEdit}><p>{profile.phone || "Telepon belum diisi"}</p><p>{profile.email || "Email belum diisi"}</p><p>{profile.websiteUrl || "Situs belum diisi"}</p></ReviewBlock>
    </div>
  );
}

function DataSummary({ profile, onEdit }) {
  return (
    <div>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div><p className="text-sm font-bold text-[#B46F00]">Profil sekolah tersimpan</p><h1 className="mt-1 text-3xl font-extrabold text-ink sm:text-4xl">Data Sekolah</h1><p className="mt-2 max-w-2xl font-medium text-ink/60">Kelola informasi sekolah yang akan ditampilkan kepada keluarga.</p>{profile.updatedAt ? <p className="mt-2 text-xs font-bold text-ink/40">Terakhir diperbarui {formatDate(profile.updatedAt)}</p> : null}</div>
        <button type="button" onClick={() => onEdit(1)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-ink px-5 text-sm font-extrabold text-white hover:bg-[#2c2363] focus-ring"><Pencil className="h-4 w-4" />Edit Data Sekolah</button>
      </div>
      <div className="mt-7 grid gap-4 lg:grid-cols-2">
        <ReviewBlock title="Informasi Dasar" step={1} onEdit={onEdit}><p className="text-lg font-extrabold text-ink">{profile.name}</p><p>{profile.educationLevels.join(", ")} · {profile.city}</p><p className="mt-2">{profile.description}</p></ReviewBlock>
        <ReviewBlock title="Akademik & Program" step={2} onEdit={onEdit}><p>{profile.curriculum}</p><p>{profile.accreditation ? `Akreditasi ${profile.accreditation}` : "Akreditasi belum diisi"}</p><div className="mt-2 flex flex-wrap gap-2">{profile.interests.map((item) => <span key={item} className="rounded-lg bg-mint/20 px-2.5 py-1 text-xs font-bold text-ink">{item}</span>)}</div></ReviewBlock>
        <ReviewBlock title="Biaya" step={3} onEdit={onEdit}><div className="grid grid-cols-2 gap-3"><div><p className="text-xs uppercase text-ink/40">SPP Bulanan</p><p className="mt-1 font-extrabold text-ink">{formatRupiah(Number(profile.monthlyTuition))}</p></div><div><p className="text-xs uppercase text-ink/40">Uang Pangkal</p><p className="mt-1 font-extrabold text-ink">{profile.entranceFee ? formatRupiah(Number(profile.entranceFee)) : "Belum diisi"}</p></div></div></ReviewBlock>
        <ReviewBlock title="Fasilitas & Ekstrakurikuler" step={4} onEdit={onEdit}><p>{profile.facilities.join(", ")}</p><p className="mt-2">{profile.extracurriculars.join(", ") || "Ekstrakurikuler belum diisi"}</p></ReviewBlock>
        <ReviewBlock title="Galeri" step={5} onEdit={onEdit}><div className="grid grid-cols-3 gap-2">{profile.images.slice(0, 6).map((image, index) => <img key={`${image.slice(0, 24)}-${index}`} src={image} alt={`Foto sekolah ${index + 1}`} className="aspect-[4/3] w-full rounded-lg object-cover" />)}</div></ReviewBlock>
        <ReviewBlock title="Kontak & Lokasi" step={5} onEdit={onEdit}><p>{profile.address}</p><p className="mt-2">{profile.phone}</p><p>{profile.email}</p>{profile.websiteUrl ? <a href={profile.websiteUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 font-bold text-purple hover:underline">{profile.websiteUrl}<ExternalLink className="h-3.5 w-3.5" /></a> : null}</ReviewBlock>
      </div>
    </div>
  );
}

function DataSchoolPage({ profile, saveProfile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const stepParam = new URLSearchParams(location.search).get("step");
  const requestedStep = Number(stepParam) || 1;
  const [editingStep, setEditingStep] = useState(profile.completed ? null : 1);

  useEffect(() => {
    if (stepParam && profile.completed) setEditingStep(requestedStep);
  }, [stepParam, requestedStep, profile.completed]);

  const handleSave = (value) => {
    saveProfile(value);
    setEditingStep(null);
    navigate("/dashboard/sekolah/data", { replace: true });
  };

  if (profile.completed && editingStep == null) return <DataSummary profile={profile} onEdit={setEditingStep} />;
  return (
    <div>
      <div className="mb-6"><p className="text-sm font-bold text-[#B46F00]">Satu profil sekolah</p><h1 className="mt-1 text-3xl font-extrabold text-ink sm:text-4xl">Data Sekolah</h1><p className="mt-2 max-w-2xl font-medium text-ink/60">Lengkapi dan kelola informasi sekolah yang akan ditampilkan kepada keluarga.</p></div>
      <SchoolDataForm profile={profile} onSave={handleSave} initialStep={editingStep || 1} onCancel={profile.completed ? () => setEditingStep(null) : null} />
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, helper, accent }) {
  return <article className="rounded-[20px] border border-ink/10 bg-white p-4 shadow-sm sm:p-5"><span className={`flex h-10 w-10 items-center justify-center rounded-[13px] ${accent}`}><Icon className="h-5 w-5" aria-hidden="true" /></span><p className="mt-4 text-xs font-bold uppercase text-ink/45">{label}</p><p className="mt-1 text-2xl font-extrabold text-ink">{value}</p><p className="mt-1 text-xs font-medium text-ink/50">{helper}</p></article>;
}

function Overview({ profile, representativeName }) {
  const completion = getCompletion(profile);
  const tasks = getTasks(profile);
  return (
    <>
      <div><p className="text-sm font-bold text-[#B46F00]">Dashboard sekolah</p><h1 className="mt-1 text-3xl font-extrabold text-ink sm:text-4xl">Selamat datang, {representativeName}</h1><p className="mt-2 max-w-2xl font-medium text-ink/60">Kelola informasi dan aktivitas sekolah Anda di MySchoolMate.</p></div>
      <section className="mt-7 grid grid-cols-2 gap-3 xl:grid-cols-4 xl:gap-4" aria-label="Ringkasan dashboard sekolah">
        <MetricCard icon={School} label="Kelengkapan Profil" value={`${completion}%`} helper={`${completionChecks.length - tasks.length} dari ${completionChecks.length} bagian selesai`} accent="bg-[#FFF3D7] text-[#B46F00]" />
        <MetricCard icon={BarChart3} label="Dilihat Keluarga" value="Belum tersedia" helper="Analitik belum terhubung" accent="bg-mint/20 text-ink" />
        <MetricCard icon={ClipboardList} label="Minat Pendaftaran" value="Belum ada" helper="Belum ada pendaftaran" accent="bg-purple/10 text-purple" />
        <MetricCard icon={MessageSquareText} label="Review Sekolah" value="Belum ada" helper="Belum ada review" accent="bg-coral/15 text-coral" />
      </section>

      {!profile.completed ? <section className="mt-7 flex flex-col gap-5 rounded-[22px] border border-[#F7AB2D]/30 bg-[#FFF9EA] p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"><div className="flex items-start gap-4"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#F7AB2D] text-ink"><School className="h-5 w-5" /></span><div><h2 className="font-extrabold text-ink">Lengkapi Data Sekolah</h2><p className="mt-1 text-sm font-medium leading-relaxed text-ink/60">Tambahkan informasi dasar agar profil sekolah dapat ditampilkan dengan lebih lengkap.</p></div></div><Button to="/dashboard/sekolah/data?step=1" variant="yellow" className="min-h-11 shrink-0">Mulai Lengkapi Data<ArrowRight className="h-4 w-4" /></Button></section> : null}

      <div className="mt-7 grid gap-5 xl:grid-cols-[1.35fr_0.85fr]">
        <section className="rounded-[22px] border border-ink/10 bg-white p-5 shadow-sm sm:p-6"><div className="flex items-center justify-between gap-4"><div><h2 className="text-xl font-extrabold text-ink">Performa Profil Sekolah</h2><p className="mt-1 text-sm font-medium text-ink/50">Ringkasan berdasarkan data profil yang benar-benar tersimpan.</p></div><Sparkles className="h-5 w-5 text-[#F7AB2D]" /></div><div className="mt-6"><div className="flex items-center justify-between text-sm font-bold text-ink"><span>Kelengkapan profil</span><span>{completion}%</span></div><div className="mt-2 h-3 overflow-hidden rounded-full bg-ink/8"><div className="h-full rounded-full bg-[#F7AB2D]" style={{ width: `${completion}%` }} /></div></div><div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3"><div className="rounded-[16px] bg-warm p-4"><FileImage className="h-5 w-5 text-mint" /><p className="mt-3 text-2xl font-extrabold text-ink">{profile.images.length}</p><p className="text-xs font-bold text-ink/45">Foto sekolah</p></div><div className="rounded-[16px] bg-warm p-4"><CheckCircle2 className="h-5 w-5 text-purple" /><p className="mt-3 text-2xl font-extrabold text-ink">{profile.facilities.length}</p><p className="text-xs font-bold text-ink/45">Fasilitas</p></div><div className="col-span-2 rounded-[16px] bg-warm p-4 sm:col-span-1"><Clock3 className="h-5 w-5 text-coral" /><p className="mt-3 text-sm font-extrabold text-ink">{profile.updatedAt ? formatDate(profile.updatedAt) : "Belum pernah"}</p><p className="text-xs font-bold text-ink/45">Terakhir diperbarui</p></div></div></section>
        <section className="rounded-[22px] border border-ink/10 bg-white p-5 shadow-sm sm:p-6"><h2 className="text-xl font-extrabold text-ink">Tugas Selanjutnya</h2><p className="mt-1 text-sm font-medium text-ink/50">Tugas dibuat dari informasi yang masih kosong.</p><div className="mt-5 space-y-2">{tasks.length ? tasks.slice(0, 5).map((task) => <Link key={task.label} to={`/dashboard/sekolah/data?step=${task.step}`} className="flex items-center justify-between gap-3 rounded-[14px] bg-warm px-4 py-3 text-sm font-bold text-ink transition hover:bg-[#FFF3D7] focus-ring"><span>{task.label}</span><ArrowRight className="h-4 w-4 shrink-0 text-[#B46F00]" /></Link>) : <div className="rounded-[16px] bg-mint/15 p-4 text-sm font-bold text-ink"><CheckCircle2 className="mb-2 h-5 w-5 text-mint" />Semua informasi utama sudah lengkap.</div>}</div></section>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <section className="rounded-[22px] border border-ink/10 bg-white p-5 shadow-sm sm:p-6"><h2 className="text-xl font-extrabold text-ink">Aktivitas Terbaru</h2>{profile.activities.length ? <div className="mt-5 space-y-3">{profile.activities.slice(0, 5).map((activity) => <div key={activity.id} className="flex items-start gap-3 rounded-[14px] bg-warm p-4"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-mint" /><div><p className="text-sm font-bold text-ink">{activity.label}</p><p className="mt-1 text-xs font-medium text-ink/45">{formatDate(activity.createdAt)}</p></div></div>)}</div> : <div className="mt-5 flex min-h-[150px] flex-col items-center justify-center rounded-[16px] bg-warm text-center"><Clock3 className="h-7 w-7 text-ink/20" /><p className="mt-3 font-extrabold text-ink">Belum ada aktivitas terbaru.</p></div>}</section>
        <section className="rounded-[22px] border border-ink/10 bg-white p-5 shadow-sm sm:p-6"><h2 className="text-xl font-extrabold text-ink">Pesan Masuk</h2><div className="mt-5 flex min-h-[150px] flex-col items-center justify-center rounded-[16px] bg-warm text-center"><Mail className="h-7 w-7 text-ink/20" /><p className="mt-3 font-extrabold text-ink">Belum ada pesan masuk.</p><Link to="/dashboard/sekolah/pesan" className="mt-3 text-sm font-extrabold text-purple hover:underline focus-ring">Buka halaman pesan</Link></div></section>
      </div>
    </>
  );
}

function SettingsPage({ representativeName }) {
  const [communication, setCommunication] = useState(false);
  return <div><h1 className="text-3xl font-extrabold text-ink sm:text-4xl">Pengaturan</h1><p className="mt-2 font-medium text-ink/60">Pengaturan lokal untuk akun perwakilan sekolah.</p><section className="mt-6 rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm"><h2 className="font-extrabold text-ink">Informasi akun</h2><p className="mt-2 text-sm font-medium text-ink/55">Nama perwakilan: {representativeName}. Perubahan akun belum terhubung ke backend.</p><label className="mt-5 flex items-start gap-3 rounded-[16px] bg-warm p-4"><input type="checkbox" checked={communication} onChange={(e) => setCommunication(e.target.checked)} className="mt-1 h-4 w-4 accent-[#F7AB2D]" /><span className="text-sm font-medium text-ink/65">Tandai preferensi komunikasi ketika layanan tersebut tersedia.</span></label></section></div>;
}

function HelpPage() {
  return <div><h1 className="text-3xl font-extrabold text-ink sm:text-4xl">Bantuan</h1><p className="mt-2 font-medium text-ink/60">Temukan informasi untuk melanjutkan pengelolaan sekolah.</p><div className="mt-6 grid gap-4 md:grid-cols-2"><Link to="/tentang" className="rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 focus-ring"><CircleHelp className="h-7 w-7 text-[#B46F00]" /><h2 className="mt-4 text-lg font-extrabold text-ink">Tentang MySchoolMate</h2><p className="mt-2 text-sm font-medium text-ink/55">Pelajari tujuan dan cara kerja MySchoolMate.</p></Link><Link to="/dashboard/sekolah/data" className="rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 focus-ring"><School className="h-7 w-7 text-mint" /><h2 className="mt-4 text-lg font-extrabold text-ink">Kelola Data Sekolah</h2><p className="mt-2 text-sm font-medium text-ink/55">Kembali ke pengelolaan informasi sekolah.</p></Link></div></div>;
}

export default function SchoolDashboard() {
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(readStoredProfile);
  const representativeName = useMemo(readRepresentativeName, []);

  const saveProfile = (value) => {
    const now = new Date().toISOString();
    const next = normalizeProfile({
      ...value,
      completed: true,
      updatedAt: now,
      activities: [{ id: now, label: profile.completed ? "Data sekolah diperbarui" : "Profil sekolah dilengkapi", createdAt: now }, ...profile.activities].slice(0, 20),
    });
    setProfile(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* Continue with in-memory state. */ }
  };

  let content;
  if (pathname.includes("/data")) content = <DataSchoolPage profile={profile} saveProfile={saveProfile} />;
  else if (pathname.endsWith("/pendaftaran")) content = <DashboardEmptyState icon={ClipboardList} title="Belum ada minat pendaftaran." description="Data pendaftaran keluarga akan tampil di sini ketika fitur tersebut tersedia." />;
  else if (pathname.endsWith("/statistik")) content = <DashboardEmptyState icon={BarChart3} title="Statistik belum tersedia." description="Statistik akan muncul setelah profil sekolah mulai menerima aktivitas yang dapat diukur." />;
  else if (pathname.endsWith("/review")) content = <DashboardEmptyState icon={MessageSquareText} title="Belum ada review untuk sekolah Anda." description="Review keluarga akan tampil di sini saat fitur review telah digunakan." />;
  else if (pathname.endsWith("/pesan")) content = <DashboardEmptyState icon={Mail} title="Belum ada pesan masuk." description="Pertanyaan dari keluarga akan tampil di sini ketika fitur pesan tersedia." />;
  else if (pathname.endsWith("/pengaturan")) content = <SettingsPage representativeName={representativeName} />;
  else if (pathname.endsWith("/bantuan")) content = <HelpPage />;
  else content = <Overview profile={profile} representativeName={representativeName} />;

  return (
    <div className="min-h-screen bg-warm lg:flex">
      <SEO title="Dashboard Sekolah | MySchoolMate" canonicalPath="/dashboard/sekolah" noIndex />
      <SchoolDashboardSidebar pathname={pathname} open={sidebarOpen} onClose={() => setSidebarOpen(false)} schoolName={profile.name} representativeName={representativeName} />
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-ink/10 bg-white/95 px-4 backdrop-blur lg:hidden">
          <Link to="/" className="rounded-lg focus-ring" aria-label="Kembali ke beranda MySchoolMate"><img src={brandLogo} alt="MySchoolMate" width="288" height="43" className="h-7 w-auto" /></Link>
          <div className="flex items-center gap-3"><span className="hidden text-sm font-extrabold text-ink sm:block">Dashboard Sekolah</span><button type="button" onClick={() => setSidebarOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-xl border border-ink/15 bg-white text-ink focus-ring" aria-label="Buka menu dashboard" aria-expanded={sidebarOpen}><Menu className="h-5 w-5" /></button></div>
        </header>
        <main className="mx-auto max-w-[1380px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8 xl:px-10 xl:py-10">{content}</main>
      </div>
    </div>
  );
}
